import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Row, Table, Popconfirm, Radio, Button } from 'antd';
import ExportJsonExcel from 'js-export-excel';

import generatePath from '~settings/routing';
import formatPrice from '~helpers/currency';
import { communicationStatus } from '~client/constants';
import InvoicedModal from '~pages/dashboard/billing/receive/invoiced-modal';
import { dayFormatType, formatDay } from '~helpers/day';
import { bookingJourneyType } from 'common/constants/booking';
import { listOrderReceivablesExport } from '~actions/billing';

@withTranslation()
export default class ReceiveTable extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      exportLoading: false,
    };
  }

  getColumns = () => {
    const { t } = this.props;
    const columns = [
      {
        title: t('cms.billing.receive.table.receivables_number.field'),
        dataIndex: 'id',
        key: 'id',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.referenceId ? record.referenceId : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.receivables_type.field'),
        dataIndex: 'receivablesType',
        key: 'receivablesType',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.receivableType
              ? t(
                  `cms.billing.receive.table.receivables_type.${record.receivableType.toLowerCase()}`,
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.receivables_method.field'),
        dataIndex: 'receivablesMethod',
        key: 'receivablesMethod',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.receivableMethod
              ? t(
                  `cms.billing.receive.table.receivables_method.${record.receivableMethod.toLowerCase()}`,
                )
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.receivables_status.field'),
        dataIndex: 'status',
        key: 'status',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.status
              ? t(`cms.billing.receive.table.receivables_status.${record.status.toLowerCase()}`)
              : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.landlord.field'),
        dataIndex: 'landlord',
        key: 'landlord',
        render: (text, record) => (
          <div className="receive__table-field">{record.landlord ? record.landlord.name : '-'}</div>
        ),
      },
      {
        title: t('cms.billing.receive.table.property.field'),
        dataIndex: 'property',
        key: 'property',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.order && record.order.property ? record.order.property.name : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.receivables_amount.field'),
        dataIndex: 'receivablesAmount',
        key: 'receivablesAmount',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.amount ? formatPrice(record.amount, record.currency) : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.last_invoice_number.field'),
        dataIndex: 'invoiceNumber',
        key: 'invoiceNumber',
        render: (text, record) => (
          <span className="receive__table-field">{record.invoiceNumber || '-'}</span>
        ),
      },
      {
        title: t('cms.billing.receive.table.invoice_date.field'),
        dataIndex: 'invoiceDate',
        key: 'invoiceDate',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.invoicedAt ? formatDay(dayFormatType.UKFORMAT, record.invoicedAt) : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.paid_date.field'),
        dataIndex: 'paidDate',
        key: 'paidDate',
        render: (text, record) => (
          <span className="receive__table-field">
            {record.paidAt ? formatDay(dayFormatType.UKFORMAT, record.paidAt) : '-'}
          </span>
        ),
      },
      {
        title: t('cms.billing.receive.table.action.field'),
        key: 'action',
        fixed: 'right',
        className: 'receive__action',
        render: (text, record) => (
          <Row
            className={classNames('receive__action-row', {
              'receive__action-row--invoiced':
                ['PENDING_INVOICE', 'INVOICED'].indexOf(record.status) !== -1,
            })}
          >
            <button
              className="receive__table-btn"
              onClick={() => {
                this.handleViewBtn(record.referenceId);
              }}
            >
              {t('cms.billing.transfer.table.action.view.btn')}
            </button>
            <If condition={record.status === 'PENDING_INVOICE'}>
              <InvoicedModal
                t={t}
                onInvoiced={number => this.handleInvoiced(record.id, number, 'INVOICED')}
                communication={this.props.communication}
                type="tableBtn"
              />
            </If>
            <If condition={record.status === 'INVOICED'}>
              <Popconfirm
                overlayStyle={{ maxWidth: 260 }}
                placement="top"
                title={t('cms.billing.receive.paid.modal.title')}
                onConfirm={() => {
                  this.handleInvoiced(record.id, null, 'PAID');
                }}
                okText={t('cms.properties.edit.btn.yes')}
                okType="danger"
                cancelText={t('cms.properties.edit.btn.no')}
                arrowPointAtCenter
              >
                <button className="receive__table-btn receive__table-btn--invoiced">
                  {t('cms.billing.receive.table.action.paid.btn')}
                </button>
              </Popconfirm>
            </If>
          </Row>
        ),
      },
    ];

    return columns;
  };

  handleViewBtn = id => {
    this.props.history.push(generatePath('billing.receiveDetail', { id }));
  };

  handleInvoiced = (id, num, status, onSuccess = () => {}) => {
    const data = { id, status };

    if (num) {
      data.invoiceNumber = num;
    }

    this.props.pendingReceivables(data, onSuccess);
  };

  handleExportToExcel = exportData => {
    const sheetHeader = [
      'Booking Number',
      'Student Name',
      'Email Address',
      'City',
      'Landlord Name',
      'Property Name',
      'Booking Created Date',
      'Booking Complete Date',
      'Move-in date',
      'Move-out date',
      'Room Name',
      'Unit Price',
      'Total Rent',
      'Total Commission',
      'Commission Tier',
      'Current Status',
      'Currency',
      'Invoice Number',
      'Invoiced at',
    ];

    const xlsxData = exportData.map(data => {
      return {
        bookingNumber: data?.order?.referenceId,
        studentName:
          data?.order?.student?.passportFirstName || data?.order?.student?.passportLastName
            ? `${data?.order?.student?.passportFirstName} ${data?.order?.student?.passportLastName}`
            : '-',
        emailAddress: data?.order?.student?.emailAddress,
        city: data?.order?.property?.city?.name,
        landlordName: data?.landlord?.name,
        propertyName: data?.order?.property?.name,
        bookingCreatedDate: data?.order?.createdAt
          ? formatDay(dayFormatType.UKFORMAT, data?.order?.createdAt)
          : '-',
        bookingCompletedDate: data?.order?.completedAt
          ? formatDay(dayFormatType.UKFORMAT, data?.order?.completedAt)
          : '-',
        moveInDate: data?.order?.moveInDate
          ? formatDay(dayFormatType.UKFORMAT, data?.order?.moveInDate)
          : '-',
        moveOutDate: data?.order?.moveOutDate
          ? formatDay(dayFormatType.UKFORMAT, data?.order?.moveOutDate)
          : '-',
        roomName: data?.order?.unitTypeName,
        unitPrice: data?.order?.listingMinPrice,
        totalRent: data?.order?.totalRental,
        totalCommission: data?.commission?.totalPrice ?? '-',
        commissionTier: data?.commission?.commissionTierValue ?? '-',
        currentStatus: data?.status,
        currency: data?.currency,
        invoiceNumber: data?.invoiceNumber,
        invoicedAt: data?.invoicedAt ? formatDay(dayFormatType.UKFORMAT, data?.invoicedAt) : '-',
      };
    });
    const option = {};
    option['fileName'] = 'Receivable Management';

    option['datas'] = [
      {
        sheetData: xlsxData,
        sheetHeader,
      },
    ];
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
    this.setState({ exportLoading: false });
  };

  handleExport = () => {
    this.setState({ exportLoading: true });
    listOrderReceivablesExport(
      {
        ...this.props.filters,
        pageNumber: 1,
        pageSize: this.props.list?.total ?? 10,
        bookingJourneys: !this.props.filters?.bookingJourney
          ? [bookingJourneyType.SEMI_AUTOMATIC, bookingJourneyType.INSTANT_BOOK]
          : this.props.filters?.bookingJourneys,
      },
      res => {
        if (res.listOrderReceivables?.edges?.length > 0) {
          this.handleExportToExcel(res.listOrderReceivables?.edges);
        }
      },
      () => {
        this.setState({ exportLoading: false });
      },
    );
  };

  render() {
    const { exportLoading } = this.state;
    const { t, list, communication, filters, handleBookingTypeChange } = this.props;

    return (
      <div className="receive__table-content">
        <div className="receive__table-content--header">
          <Radio.Group value={filters.bookingJourney} onChange={handleBookingTypeChange}>
            <Radio.Button value={null}>
              {t('cms.billing.receive.table.booking_type_all')}
            </Radio.Button>
            <Radio.Button value={bookingJourneyType.SEMI_AUTOMATIC}>
              {t('properties_booking_type_semi_automatic')}
            </Radio.Button>
            <Radio.Button value={bookingJourneyType.INSTANT_BOOK}>
              {t('properties_booking_type_instant_book')}
            </Radio.Button>
          </Radio.Group>

          <div className="transfer__table__left-wrapper">
            <h4 className="receive__table-title">
              {t('cms.billing.transfer.table.total_title', { totalCount: list.total || 0 })}
            </h4>
            <If condition={list?.total !== 0}>
              <Button
                loading={exportLoading}
                icon="export"
                className="transfer__table__left-wrapper__export"
                onClick={this.handleExport}
              >
                {t('cms.referral.partner.header.btn.export')}
              </Button>
            </If>
          </div>
        </div>
        <Table
          className="receive__table"
          columns={this.getColumns()}
          dataSource={list.payload || []}
          loading={
            communication &&
            communication.receiveList &&
            communication.receiveList.status === communicationStatus.FETCHING
          }
          pagination={{
            current: this.props.filters.pageNumber,
            pageSize: this.props.filters.pageSize,
            showSizeChanger: true,
            hideOnSinglePage: false,
            total: list.total,
            onChange: this.props.handlePaginationChange,
            onShowSizeChange: this.props.handlePageSizeChange,
          }}
        />
      </div>
    );
  }
}

ReceiveTable.propTypes = {
  t: PropTypes.func.isRequired,
  list: PropTypes.object,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }),
  communication: PropTypes.object,
  filters: PropTypes.object,
  handlePaginationChange: PropTypes.func,
  handlePageSizeChange: PropTypes.func,
  pendingReceivables: PropTypes.func.isRequired,
  handleBookingTypeChange: PropTypes.func.isRequired,
};

ReceiveTable.defaultProps = {
  t: () => {},
  list: {},
  history: {
    push: () => {},
  },
  communication: {},
  filters: {},
  handlePaginationChange: () => {},
  handlePageSizeChange: () => {},
  pendingReceivables: () => {},
  handleBookingTypeChange: () => {},
};
